import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, WhatsappLogo, Copy, CalendarBlank, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_FILTERS = [
  { label: '1D', days: 1 },
  { label: '7D', days: 7 },
  { label: '15D', days: 15 },
  { label: '30D', days: 30 },
];

const PRESET_CALL_CHIPS = [
  'Call initiated',
  'Called - No Answer',
  'Called - Busy',
  'Called - Switched Off',
  'Call Connected - Interested',
  'Call Connected - Not Interested',
  'Deposit Query',
  'Withdrawal Query'
];

const InactiveClientsTracker = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('dws_user') || '{}');
  const isAdmin = user.role === 'super_admin';
  const hasFullPhoneAccess = canViewFullPhone(user);

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [customDays, setCustomDays] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [activeCallModal, setActiveCallModal] = useState(null);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [callingClientId, setCallingClientId] = useState(null);
  const [callingChips, setCallingChips] = useState(PRESET_CALL_CHIPS);

  const fetchInactiveClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/inactive`, {
        params: { user_id: user.id, role: user.role, days: selectedDays }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch inactive clients:', error);
      toast.error('Failed to load inactive clients');
    } finally {
      setLoading(false);
    }
  }, [selectedDays, user.id, user.role]);

  useEffect(() => {
    if (user.id) fetchInactiveClients();
  }, [fetchInactiveClients, user.id]);

  // Fetch admin-configured calling chips from settings API
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (Array.isArray(res.data?.calling_chips) && res.data.calling_chips.length > 0) {
          setCallingChips(res.data.calling_chips);
        }
      } catch (e) {}
    };
    fetchSettings();
  }, []);

  const handlePresetFilter = (days) => {
    setShowCustom(false);
    setSelectedDays(days);
  };

  const applyCustomDays = () => {
    const d = parseInt(customDays);
    if (d > 0) {
      setSelectedDays(d);
      setShowCustom(false);
    } else {
      toast.error('Enter valid number of days');
    }
  };

  const exportPDF = () => {
    if (!filteredClients.length) {
      toast.error('No clients to export');
      return;
    }
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(16);
      doc.setTextColor(211, 16, 39);
      doc.text(`Inactive Clients Tracker (${selectedDays}+ Days)`, 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Total Clients: ${filteredClients.length} | Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 22);

      const tableHeaders = [
        ['#', 'Username', 'Client ID', 'MB', 'Mobile', 'Address', 'IFSC / Bank', 'Last Login', 'Inactive Days', 'Last Withdrawal', 'Last Deposit']
      ];

      const tableData = filteredClients.map((c, idx) => [
        idx + 1,
        c.username || '-',
        c.client_id || (c.id ? `${c.id.slice(0, 8)}...` : '-'),
        c.mb_setup || '-',
        maskPhone(c.phone, hasFullPhoneAccess) || '-',
        c.address || '-',
        c.ifsc_code ? `${c.ifsc_code}${c.bank_name ? ` (${c.bank_name})` : ''}` : '-',
        formatDate(c.last_activity),
        `${c.inactive_days}d`,
        c.last_withdrawal ? `Rs. ${Number(c.last_withdrawal).toLocaleString('en-IN')}` : '-',
        c.last_deposit ? `Rs. ${Number(c.last_deposit).toLocaleString('en-IN')}` : '-'
      ]);

      autoTable(doc, {
        startY: 27,
        head: tableHeaders,
        body: tableData,
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        alternateRowStyles: { fillColor: [248, 250, 252] }
      });

      doc.save(`inactive_clients_${selectedDays}days.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      console.error('Failed to export PDF:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportExcel = () => {
    if (!clients.length) return;
    const headers = ['S.No', 'Username', 'MB', 'Phone', 'Last Active', 'Inactive Days', 'Last Withdrawal', 'Last Deposit'];
    const rows = filteredClients.map((c, i) => [
      i + 1,
      c.username || 'N/A',
      c.mb_setup || '-',
      c.phone || 'N/A',
      c.last_active ? new Date(c.last_active).toLocaleString() : 'Never',
      `${c.inactive_days || 0}d`,
      c.last_withdrawal ? `Rs. ${c.last_withdrawal}` : '-',
      c.last_deposit ? `Rs. ${c.last_deposit}` : '-'
    ]);
    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `inactive_clients_${selectedDays}days.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Excel (CSV) downloaded!');
  };

  const copyClientInfo = (client) => {
    const info = [
      client.username,
      client.phone ? `Phone: ${client.phone}` : '',
      client.mb_setup ? `MB: ${client.mb_setup}` : '',
      client.bank_name ? `Bank: ${client.bank_name}` : '',
      client.bank_account_number ? `A/C: ${client.bank_account_number}` : '',
      client.ifsc_code ? `IFSC: ${client.ifsc_code}` : '',
      client.upi_id ? `UPI: ${client.upi_id}` : '',
      client.address ? `Address: ${client.address}` : '',
      `Inactive: ${client.inactive_days} days`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(info);
    toast.success('Copied!');
  };

  /**
   * Log a call or WhatsApp redirect:
   * 1. Opens Call Modal INSTANTLY (0ms) and shows loader button
   * 2. Posts internal message to user's DEPOSIT chat room asynchronously
   * 3. Opens phone/WA link
   */
  const logContactActivity = async (client, kind) => {
    if (!client?.id) {
      toast.error('Client info not available');
      return;
    }

    const phone = client.phone;

    // For Call: Open Popup Modal INSTANTLY (0ms delay!) and set loader
    if (kind === 'call') {
      setCallingClientId(client.id);
      setActiveCallModal({
        client,
        activityId: null,
        note: 'Call initiated',
        syncLoading: true
      });
    }

    if (kind === 'whatsapp') {
      if (!phone) {
        toast.error('No phone number available');
        return;
      }
      const cleanPhone = phone.replace(/\D/g, '');
      const whatsappNumber = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`;
      window.open(`https://wa.me/${whatsappNumber}`, '_blank');
    } else if (phone) {
      // Use anchor element for tel: — avoids page navigation issues
      const a = document.createElement('a');
      a.href = `tel:${phone}`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    // Background activity logging API call
    try {
      const res = await axios.post(`${API}/calling-reports/activity`, {
        customer_id: client.id,
        kind,
        note: kind === 'whatsapp' ? 'WhatsApp redirect' : 'Call initiated'
      });
      const activity = res.data?.activity;
      
      if (kind === 'call') {
        setActiveCallModal(prev => (prev && prev.client?.id === client.id ? {
          ...prev,
          activityId: activity?.id || null,
          syncLoading: false
        } : prev));
      }
      toast.success(kind === 'whatsapp' ? 'WhatsApp redirect logged in chat' : 'Call logged in chat');
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Unknown error';
      console.error('[InactiveClients] Failed to log activity:', detail, err?.response?.data);
      if (kind === 'call') {
        setActiveCallModal(prev => (prev ? { ...prev, syncLoading: false } : null));
      }
    } finally {
      setCallingClientId(null);
    }
  };

  const handleSaveCallNote = async () => {
    if (!activeCallModal) return;
    const { activityId, note, client } = activeCallModal;
    setIsSubmittingNote(true);
    try {
      if (activityId) {
        await axios.patch(`${API}/calling-reports/${activityId}/remark`, { note: note.trim() });
        toast.success('Calling Agent Report Note updated!');
      } else {
        // Fallback: If background activity logging is still resolving or failed, create with custom note directly
        await axios.post(`${API}/calling-reports/activity`, {
          customer_id: client?.id,
          kind: 'call',
          note: note.trim()
        });
        toast.success('Calling Agent Report Note saved!');
      }
      setActiveCallModal(null);
    } catch (err) {
      console.error('Failed to update call report note:', err);
      toast.error('Failed to update note in Calling Agent Reports');
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' });
    } catch { return '-'; }
  };

  const filteredClients = clients.filter(c =>
    c.inactive_days > 0 &&
    (c.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery))
  );

  return (
    <div className="h-screen flex flex-col bg-wa-hover">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#ea384d] to-[#d31027] text-white p-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/10 rounded-full" data-testid="back-btn">
            <ArrowLeft size={22} weight="bold" />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold">Inactive Clients</h1>
            <p className="text-xs text-white/90">{selectedDays}+ days | {filteredClients.length} clients</p>
          </div>
          {isAdmin && (
            <button
              onClick={exportPDF}
              disabled={exporting || filteredClients.length === 0}
              data-testid="download-pdf-btn"
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
            >
              <Download size={14} weight="bold" />
              {exporting ? '...' : 'PDF'}
            </button>
          )}
          {isAdmin && (
            <button
              onClick={exportExcel}
              disabled={filteredClients.length === 0}
              data-testid="download-excel-btn"
              className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
            >
              <Download size={14} weight="bold" />
              Excel
            </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-wa-panel border-b px-3 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="filter-tabs">
            {PRESET_FILTERS.map(f => (
              <button
                key={f.days}
                onClick={() => handlePresetFilter(f.days)}
                data-testid={`filter-${f.days}`}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedDays === f.days && !showCustom
                    ? 'bg-red-500 text-white'
                    : 'bg-wa-hover text-wa-text-secondary'
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => setShowCustom(!showCustom)}
              data-testid="filter-custom"
              className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                showCustom ? 'bg-red-500 text-white' : 'bg-wa-hover text-wa-text-secondary'
              }`}
            >
              <CalendarBlank size={12} /> Custom
            </button>
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-inactive"
            className="flex-1 px-3 py-1 border rounded-lg text-xs focus:ring-1 focus:ring-red-400 focus:outline-none min-w-0"
          />
        </div>
        {showCustom && (
          <div className="flex gap-2 mt-2" data-testid="custom-days-input">
            <input
              type="number"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder="Enter days"
              min="1"
              className="flex-1 px-3 py-1 border rounded-lg text-xs"
            />
            <button onClick={applyCustomDays} className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs font-medium">
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto bg-white dark:bg-[#111b21]" style={{ WebkitOverflowScrolling: 'touch', ...(!isAdmin ? { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' } : {}) }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-20 text-wa-text-secondary">
            <p className="font-medium">No inactive clients</p>
            <p className="text-sm mt-1">for {selectedDays}+ days</p>
          </div>
        ) : (
          <table className="w-full text-[11px]" data-testid="inactive-table">
            <thead className="bg-[#1e293b] text-white sticky top-0 z-10">
              <tr className="text-left font-bold uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2.5 whitespace-nowrap text-center">#</th>
                <th className="px-3 py-2.5 whitespace-nowrap">USERNAME</th>
                <th className="px-3 py-2.5 whitespace-nowrap">CLIENT ID</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-purple-300">MB</th>
                <th className="px-3 py-2.5 whitespace-nowrap">MOBILE</th>
                <th className="px-3 py-2.5 whitespace-nowrap">ADDRESS</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-blue-300">IFSC ADDRESS</th>
                <th className="px-3 py-2.5 whitespace-nowrap">LAST LOGIN</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-center">INACTIVE DAYS</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-right">LAST WITHDRAWAL</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-right">LAST DEPOSIT</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-center">CALL</th>
                <th className="px-3 py-2.5 whitespace-nowrap text-center">WA</th>
                {user?.role !== 'calling_handler' && isAdmin && <th className="px-3 py-2.5 whitespace-nowrap text-center">COPY</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredClients.map((client, idx) => (
                <tr key={client.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`inactive-row-${idx}`}>
                  <td className="px-3 py-2.5 text-center text-slate-500">{idx + 1}</td>
                  <td className="px-3 py-2.5 font-semibold text-slate-800 dark:text-slate-200 whitespace-nowrap">{client.username || '-'}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-400 whitespace-nowrap">{client.client_id || (client.id ? `${client.id.slice(0, 8)}...` : '-')}</td>

                  {/* MB Column */}
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {client.mb_setup ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50">
                        {client.mb_setup}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>

                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 dark:text-slate-300">{maskPhone(client.phone, hasFullPhoneAccess) || '-'}</td>
                  <td className="px-3 py-2.5 text-slate-500 max-w-[140px] truncate">{client.address || '-'}</td>
                  <td className="px-3 py-2.5 text-blue-600 dark:text-blue-400 max-w-[180px] truncate font-medium">{client.ifsc_code ? `${client.ifsc_code}${client.bank_name ? ` (${client.bank_name})` : ''}` : '-'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600 dark:text-slate-300">{formatDate(client.last_activity)}</td>
                  <td className="px-3 py-2.5 text-center">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50">
                      {client.inactive_days}d
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {client.last_withdrawal ? (
                      <div>
                        <div>₹{Number(client.last_withdrawal).toLocaleString('en-IN')}</div>
                        {client.last_withdrawal_date && (
                          <div className="text-[9px] text-slate-400 font-normal">{formatDate(client.last_withdrawal_date)}</div>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {client.last_deposit ? (
                      <div>
                        <div>₹{Number(client.last_deposit).toLocaleString('en-IN')}</div>
                        {client.last_deposit_date && (
                          <div className="text-[9px] text-emerald-500/70 font-normal">{formatDate(client.last_deposit_date)}</div>
                        )}
                      </div>
                    ) : '-'}
                  </td>

                  {/* Call Button */}
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => logContactActivity(client, 'call')}
                      disabled={callingClientId === client.id}
                      className="text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50 cursor-pointer"
                      data-testid={`call-btn-${idx}`}
                      title={`Call ${client.username}`}
                    >
                      {callingClientId === client.id ? (
                        <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin inline-block align-middle" />
                      ) : (
                        <Phone size={17} weight="fill" />
                      )}
                    </button>
                  </td>

                  {/* WhatsApp Button */}
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => logContactActivity(client, 'whatsapp')}
                      className="text-emerald-600 hover:text-emerald-700 transition-colors"
                      data-testid={`wa-btn-${idx}`}
                      title={`WhatsApp ${client.username}`}
                    >
                      <WhatsappLogo size={17} weight="fill" />
                    </button>
                  </td>

                  {user?.role !== 'calling_handler' && isAdmin && (
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={() => copyClientInfo(client)}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                        data-testid={`copy-btn-${idx}`}
                        title="Copy Info"
                      >
                        <Copy size={17} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Call Details & Note Popup Modal */}
      {activeCallModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#111b21] border border-slate-200 dark:border-slate-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#ea384d] to-[#d31027] text-white p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-base">Call Log & Note</h3>
                  {activeCallModal.syncLoading && (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-white/20 text-white px-2 py-0.5 rounded-full font-medium animate-pulse">
                      <span className="w-2 h-2 rounded-full bg-white animate-ping" />
                      Logging...
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/90">
                  {activeCallModal.client?.username} {activeCallModal.client?.mb_setup ? `(${activeCallModal.client.mb_setup})` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveCallModal(null)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors text-white font-bold text-sm w-7 h-7 flex items-center justify-center cursor-pointer"
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Full Phone & Actions */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Full Mobile Number
                </label>
                <div className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex items-center justify-between gap-2">
                  <span className="text-base font-bold font-mono text-slate-800 dark:text-slate-100 select-all">
                    {activeCallModal.client?.phone || 'No phone'}
                  </span>
                  <div className="flex items-center gap-2">
                    {activeCallModal.client?.phone && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(activeCallModal.client.phone);
                          toast.success('Phone number copied to clipboard!');
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                        title="Copy Phone Number"
                      >
                        <Copy size={15} />
                        <span>Copy</span>
                      </button>
                    )}
                    {activeCallModal.client?.phone && (
                      <a
                        href={`tel:${activeCallModal.client.phone}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-xs font-semibold transition-colors shadow-sm"
                      >
                        <Phone size={15} weight="fill" />
                        <span>Call</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Preset Outcome Chips */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Quick Preset Outcome Chips
                </label>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-1.5 border border-slate-100 dark:border-slate-800/60 rounded-xl bg-slate-50/50 dark:bg-slate-900/50">
                  {(callingChips || PRESET_CALL_CHIPS).map(chip => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setActiveCallModal(prev => ({ ...prev, note: chip }))}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                        activeCallModal.note === chip
                          ? 'bg-red-500 text-white shadow-sm scale-105 font-bold'
                          : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Note Text Area */}
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Calling Agent Report Note (Updates NOTE column)
                </label>
                <textarea
                  value={activeCallModal.note}
                  onChange={(e) => setActiveCallModal(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="Type custom note or choose a preset chip above..."
                  rows={3}
                  className="w-full px-3 py-2 text-xs border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              {/* Modal Footer Buttons */}
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setActiveCallModal(null)}
                  className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  Skip / Close
                </button>
                <button
                  type="button"
                  onClick={handleSaveCallNote}
                  disabled={isSubmittingNote}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#ea384d] to-[#d31027] text-white rounded-xl text-xs font-bold hover:opacity-90 transition-opacity shadow-md disabled:opacity-50 cursor-pointer"
                >
                  {isSubmittingNote ? 'Saving...' : 'Save Report Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveClientsTracker;
